// Shared catalogs + picker groups for a Mix's ingredient slots (main content,
// audio, stop screen). Both MixIngredients (post-creation editing, on the
// Mix detail page) and MixDialog (creation) need the exact same "what can I
// pick from" data — this hook is the one place that fetches it and builds
// the PlaylistContentPicker "custom groups" for each slot, so the two
// surfaces never drift in what they let an operator choose.
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Clapperboard, Grid2x2, Music, Hand } from 'lucide-react'
import {
  getAudios, getAudioPlaylists, getPlaylists, getSignageLayouts, getStopScreens, getMedia,
} from '@meno/ui/lib/api'
import { playlistThumbUrl, layoutThumbUrl } from '@/lib/playlistThumb'
import type { PickerGroup } from '@/components/PlaylistContentPicker'

export type ThumbMedia = { id: number; file_type: string; url: string }
export type MixPlaylistRow = { id: number; name: string; media?: ThumbMedia[] }
export type MixLayoutRow = { id: number; name: string; zones?: Array<{ media_ids?: number[] }> }
export type MixAudioRow = { id: number; original_name: string }
export type MixNamedRow = { id: number; name: string }
export type MixStopScreenRow = { id: number; name: string; media?: ThumbMedia[] }

export type MixCatalogs = {
  playlists: MixPlaylistRow[]
  layouts: MixLayoutRow[]
  allMedia: ThumbMedia[]
  audios: MixAudioRow[]
  audioPlaylists: MixNamedRow[]
  stopScreens: MixStopScreenRow[]
  /** Playlists + layouts, single-select — a Mix's "main content" slot. */
  contentGroups: PickerGroup[]
  /** Audio playlists + audio files, single-select. */
  audioGroups: PickerGroup[]
  /** Stop screens, single-select. */
  stopScreenGroups: PickerGroup[]
  resolvePlaylist: (id: number) => MixPlaylistRow | undefined
  resolveLayout: (id: number) => MixLayoutRow | undefined
  resolveAudio: (id: number) => MixAudioRow | undefined
  resolveAudioPlaylist: (id: number) => MixNamedRow | undefined
  resolveStopScreen: (id: number) => MixStopScreenRow | undefined
}

export function useMixCatalogs(): MixCatalogs {
  const { t } = useTranslation()
  const loadedRef = useRef(false)
  const [playlists, setPlaylists] = useState<MixPlaylistRow[]>([])
  const [layouts, setLayouts] = useState<MixLayoutRow[]>([])
  const [allMedia, setAllMedia] = useState<ThumbMedia[]>([])
  const [audios, setAudios] = useState<MixAudioRow[]>([])
  const [audioPlaylists, setAudioPlaylists] = useState<MixNamedRow[]>([])
  const [stopScreens, setStopScreens] = useState<MixStopScreenRow[]>([])

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    getPlaylists().then(r => setPlaylists(r.data || [])).catch(() => {})
    getSignageLayouts().then(r => setLayouts(r.data || [])).catch(() => {})
    getMedia(false, 'signage').then(r => setAllMedia(r.data || [])).catch(() => {})
    getAudios().then(r => setAudios(r.data || [])).catch(() => {})
    getAudioPlaylists().then(r => setAudioPlaylists(r.data || [])).catch(() => {})
    getStopScreens().then(r => setStopScreens(r.data || [])).catch(() => {})
  }, [])

  const contentGroups: PickerGroup[] = [
    {
      key: 'playlist', label: t('contentHub.tabs.playlists', 'Playlists'),
      items: playlists.map(p => ({
        id: p.id, label: p.name, thumb: playlistThumbUrl(p.media), icon: Clapperboard,
        colorClass: 'bg-purple-500/10 text-purple-500',
      })),
    },
    {
      key: 'layout', label: t('contentHub.tabs.layouts', 'Layouts'),
      items: layouts.map(l => ({
        id: l.id, label: l.name, thumb: layoutThumbUrl(l.zones, allMedia), icon: Grid2x2,
        colorClass: 'bg-blue-500/10 text-blue-500',
      })),
    },
  ]

  const audioGroups: PickerGroup[] = [
    {
      key: 'playlist', label: t('companions.audioPlaylists', 'Audio playlists'),
      items: audioPlaylists.map(p => ({
        id: p.id, label: p.name, icon: Music, colorClass: 'bg-indigo-500/10 text-indigo-500',
      })),
    },
    {
      key: 'file', label: t('companions.audioFiles', 'Audio files'),
      items: audios.map(a => ({
        id: a.id, label: a.original_name, icon: Music, colorClass: 'bg-indigo-500/10 text-indigo-500',
      })),
    },
  ]

  const stopScreenGroups: PickerGroup[] = [
    {
      key: 'stopScreen', label: t('companions.stopScreen', 'Stop screen'),
      items: stopScreens.map(s => ({
        id: s.id, label: s.name, thumb: playlistThumbUrl(s.media), icon: Hand,
        colorClass: 'bg-amber-500/10 text-amber-600',
      })),
    },
  ]

  return {
    playlists, layouts, allMedia, audios, audioPlaylists, stopScreens,
    contentGroups, audioGroups, stopScreenGroups,
    resolvePlaylist: (id) => playlists.find(p => p.id === id),
    resolveLayout: (id) => layouts.find(l => l.id === id),
    resolveAudio: (id) => audios.find(a => a.id === id),
    resolveAudioPlaylist: (id) => audioPlaylists.find(p => p.id === id),
    resolveStopScreen: (id) => stopScreens.find(s => s.id === id),
  }
}
